import type { MouseEvent, ReactNode } from "react";
import { ArrowRight } from "lucide-react";

export interface ConnectButtonProps {
  /** "ring" (direction A): a full-width row with a thin animated seal→ochre ring. "bubble" (borrowed from B): a round
   * bubble with a rotating colour ring and glow. Bubble is for connection moments only. */
  variant?: "ring" | "bubble";
  /** The provider's logo (an inline SVG); provider brand colours are fine here, nowhere else. */
  logo: ReactNode;
  /** "Continue with Google" (ring) or "Google" (bubble). */
  label: string;
  /** One line under the label: "Google Workspace or Gmail". */
  sub?: ReactNode;
  /** A small chip on the right of the ring variant ("Most used"). */
  badge?: ReactNode;
  /** The words on the bubble's pill. */
  cta?: string;
  /** Quiet: the ring and glow go neutral and still. Use for the second choice beside a recommended one. */
  quiet?: boolean;
  /** An OAuth or set-up address: renders a link. Otherwise pass onClick. */
  href?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  busy?: boolean;
}

/**
 * ConnectButton: the button for joining Ogma to another account (a Google or Microsoft mailbox, WhatsApp, HubSpot,
 * Pipedrive, Buffer). Use `ring` in set-up lists and `bubble` for the big moment on a dedicated connect step. Both
 * stop animating under prefers-reduced-motion. Never use either for an ordinary action: that is Button.
 */
export function ConnectButton({ variant = "ring", logo, label, sub, badge, cta = "Connect", quiet = false, href, onClick, disabled = false, busy = false }: ConnectButtonProps) {
  const name = busy ? `${label}, connecting` : undefined;
  if (variant === "bubble") {
    const inner = (
      <>
        <span className="cbtn-bubble-ring" aria-hidden="true">
          <span className="cbtn-bubble-in">{logo}</span>
        </span>
        <span className="cbtn-bubble-text">
          <span className="cbtn-bubble-label">{label}</span>
          {sub && <span className="cbtn-sub">{sub}</span>}
        </span>
        <span className="cbtn-bubble-cta">{busy ? "Connecting…" : cta}</span>
      </>
    );
    const cls = `cbtn-bubble${quiet ? " is-quiet" : ""}`;
    return href && !disabled ? (
      <a className={cls} href={href} onClick={onClick} aria-label={name}>
        {inner}
      </a>
    ) : (
      <button type="button" className={cls} onClick={onClick} disabled={disabled || busy} aria-label={name}>
        {inner}
      </button>
    );
  }
  const inner = (
    <>
      <span className="cbtn-logo" aria-hidden="true">
        {logo}
      </span>
      <span className="cbtn-text">
        <span className="cbtn-label">{busy ? "Connecting…" : label}</span>
        {sub && <span className="cbtn-sub">{sub}</span>}
      </span>
      {badge && <span className="cbtn-badge">{badge}</span>}
      <ArrowRight className="cbtn-arrow" size={18} strokeWidth={2} aria-hidden="true" focusable="false" />
    </>
  );
  return (
    <span className={`cbtn-ring${quiet ? " is-quiet" : ""}`}>
      {href && !disabled ? (
        <a className="cbtn" href={href} onClick={onClick} aria-label={name}>
          {inner}
        </a>
      ) : (
        <button type="button" className="cbtn" onClick={onClick} disabled={disabled || busy} aria-label={name}>
          {inner}
        </button>
      )}
    </span>
  );
}
